/**
 * Offline cache for the Care clinical module — separate from the POS
 * offline cache (`lib/offlineDB.js`) since the data shapes are different
 * (plain API response snapshots + a queue of clinical decisions that
 * couldn't reach the server yet, rather than product/sale rows).
 *
 * This just lets the UI keep showing/working with the last-seen data
 * through a connectivity drop. The whole thing is AES-GCM encrypted at
 * rest in IndexedDB (reusing lib/offlineCrypto.js) so cached clinical data
 * isn't sitting in plain browser storage.
 */
import { encryptValue, decryptValue } from '../lib/offlineCrypto';

const DB_NAME = 'pharmxai_care_offline';
const DB_VERSION = 1;

const STORES = {
  CACHE: 'cache', // generic key/value snapshots: identity, per-path request lists, signer certs
  PENDING_ACTIONS: 'pending_actions', // queued clinical decisions awaiting a connection
};

let db = null;

const getDB = () => {
  if (db) return Promise.resolve(db);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => { db = request.result; resolve(db); };
    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      if (!database.objectStoreNames.contains(STORES.CACHE)) {
        database.createObjectStore(STORES.CACHE, { keyPath: 'cache_key' });
      }
      if (!database.objectStoreNames.contains(STORES.PENDING_ACTIONS)) {
        database.createObjectStore(STORES.PENDING_ACTIONS, { keyPath: 'local_id', autoIncrement: true });
      }
    };
  });
};

/** Cache an arbitrary snapshot (identity, a `/requests` response, a signer cert...) under `key`. */
export const cacheCareSnapshot = async (key, value) => {
  const enc = await encryptValue(value);
  const database = await getDB();
  const tx = database.transaction(STORES.CACHE, 'readwrite');
  tx.objectStore(STORES.CACHE).put({ cache_key: key, enc, cached_at: new Date().toISOString() });
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
};

export const getCachedCareSnapshot = async (key) => {
  const database = await getDB();
  const tx = database.transaction(STORES.CACHE, 'readonly');
  const request = tx.objectStore(STORES.CACHE).get(key);
  const row = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  if (!row) return null;
  return decryptValue(row.enc);
};

/** Queue a clinical decision for submission once back online. */
export const queueCareAction = async (action) => {
  const enc = await encryptValue(action);
  const database = await getDB();
  const tx = database.transaction(STORES.PENDING_ACTIONS, 'readwrite');
  const request = tx.objectStore(STORES.PENDING_ACTIONS).add({ enc, queued_at: new Date().toISOString() });
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getPendingCareActions = async () => {
  const database = await getDB();
  const tx = database.transaction(STORES.PENDING_ACTIONS, 'readonly');
  const request = tx.objectStore(STORES.PENDING_ACTIONS).getAll();
  const rows = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  const decrypted = await Promise.all(rows.map(async (r) => ({
    ...(await decryptValue(r.enc)), local_id: r.local_id, queued_at: r.queued_at,
  })));
  return decrypted.filter((a) => a.method);
};

export const removePendingCareAction = async (localId) => {
  const database = await getDB();
  const tx = database.transaction(STORES.PENDING_ACTIONS, 'readwrite');
  tx.objectStore(STORES.PENDING_ACTIONS).delete(localId);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
};

export default {
  cacheCareSnapshot,
  getCachedCareSnapshot,
  queueCareAction,
  getPendingCareActions,
  removePendingCareAction,
};
